import React, {
  type CSSProperties,
  type ReactNode,
  useMemo,
  useState
} from "react";
import "./styles.scss";
import KC_Heading4 from "../typography/KC-Heading/KC_Heading4";
import KC_Tag from "../KC_Tag/KC_Tag";
import KC_UIText from "../typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../ds_commons";
import TSUtils from "../../utils/helpers/TSUtils";
import KC_Heading1 from "../typography/KC-Heading/KC_Heading1";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import CurrencyProp from "../../app/04_Analytics/components/model/CurrencyProp";

interface Props {
  title: string;
  text?: string | number;
  bigText?: string | number;
  variationText?: string;
  data1Title?: string;
  data1Number?: number;
  data1Tooltip?: string;
  data2Title?: string;
  data2Number?: number;
  data2Tooltip?: string;
  data3Title?: string;
  data3Number?: number;
  data3Tooltip?: string;
  data1IsCurrency?: boolean;
  data2IsCurrency?: boolean;
  data3IsCurrency?: boolean;
  children?: ReactNode;
  className?: string;
  currency?: CurrencyProp;
}

export default function KC_DashboardCard(props: Props) {
  const hasData1 = useMemo(() => {
    return (
      !!props.data1Number &&
      props.data1Number !== undefined &&
      props.data1Number !== null
    );
  }, [props]);

  const hasData2 = useMemo(() => {
    return (
      !!props.data2Number &&
      props.data2Number !== undefined &&
      props.data2Number !== null
    );
  }, [props]);

  const hasData3 = useMemo(() => {
    return (
      !!props.data3Number &&
      props.data3Number !== undefined &&
      props.data3Number !== null
    );
  }, [props]);

  const [number1Style, number2Style, number3Style] = useMemo<
    [CSSProperties, CSSProperties, CSSProperties]
  >(() => {
    const data1 = props.data1Number || 0;
    const data2 = props.data2Number || 0;
    const data3 = props.data3Number || 0;

    const total = data1 + data2 + data3;

    if (total > 0) {
      const n1 = Math.trunc((data1 / total) * 100);
      const n2 = Math.trunc((data2 / total) * 100);
      const n3 = 100 - n1 - n2;

      return [
        { width: `${n1 || 5}%` },
        { width: `${n2 || 5}%` },
        { width: `${n3 || 5}%` }
      ];
    }

    return [{ width: "5%" }, { width: "5%" }, { width: "5%" }];
  }, [props]);

  return (
    <div
      className={`d-flex flex-column kc-card-dashboard ${props.className || ""}`}
    >
      <KC_Heading4 text={props.title} />
      <div className="d-flex flex-row align-items-center ds-gap-column-standard">
        {!!props.text && <KC_UIText text={props.text} weight="reg" />}
        {!!props.variationText && (
          <KC_Tag text={props.variationText} type="number-increase" />
        )}
        {!!props.bigText && <KC_Heading1 primary={true} text={props.bigText} />}
      </div>
      <div className="d-flex flex-column flex-grow-1 ds-gap-row-xs">
        <div className="d-flex flex-row flex-grow-1 ds-gap-column-xs dashboard-progress-bar">
          {hasData1 && !!props.data1Number && (
            <div
              className="dashboard-progress-bar-number-1"
              style={number1Style}
            ></div>
          )}
          {hasData2 && !!props.data2Number && (
            <div
              className="dashboard-progress-bar-number-2"
              style={number2Style}
            ></div>
          )}
          {hasData3 && !!props.data3Number && (
            <div
              className="dashboard-progress-bar-number-3"
              style={number3Style}
            ></div>
          )}
        </div>
        <div className="d-flex flex-column flex-grow-1 ds-gap-row-xs">
          {hasData1 && (
            <ProgressBarDetail
              title={props.data1Title}
              number={props.data1Number}
              className="dashboard-progress-bar-color-box-1"
              isCurrency={props.data1IsCurrency}
              tooltip={props.data1Tooltip}
              currency={props.currency}
            />
          )}
          {hasData2 && (
            <ProgressBarDetail
              title={props.data2Title}
              number={props.data2Number}
              className="dashboard-progress-bar-color-box-2"
              isCurrency={props.data2IsCurrency}
              tooltip={props.data2Tooltip}
              currency={props.currency}
            />
          )}
          {hasData3 && (
            <ProgressBarDetail
              title={props.data3Title}
              number={props.data3Number}
              className="dashboard-progress-bar-color-box-3"
              isCurrency={props.data3IsCurrency}
              tooltip={props.data3Tooltip}
              currency={props.currency}
            />
          )}
        </div>
      </div>

      {props.children}
    </div>
  );
}

interface ProgressBarDetailProps {
  title?: string;
  number?: number;
  className: string;
  isCurrency?: boolean;
  tooltip?: string;
  currency?: CurrencyProp;
}

function ProgressBarDetail(props: ProgressBarDetailProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const iconRef = React.useRef<HTMLDivElement>(null);

  const number = useMemo(() => {
    if (props.isCurrency && props.currency) {
      return TSUtils.formatAmountUsingIntl(
        props.number || 0,
        props.currency.subUnits,
        props.currency.trigram
      );
    }

    return props.number;
  }, [props.number, props.currency]);

  // Handle click outside to close tooltip
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showTooltip &&
        tooltipRef.current &&
        iconRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !iconRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div className="d-flex align-items-center dashboard-progress-bar-detail">
      <div className={props.className} />
      <KC_UIText weight={KC_Weights.reg} text={props.title} />
      {!!props.tooltip && (
        <div className="position-relative mx-1" ref={iconRef}>
          <FontAwesomeIcon
            icon={faInfoCircle}
            size="sm"
            style={{
              color: "#6c757d",
              cursor: "pointer"
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
          />
          {showTooltip && (
            <div
              ref={tooltipRef}
              className="dashboard-tooltip"
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%) translateY(-8px)",
                backgroundColor: "#333",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                zIndex: 1000,
                minWidth: "200px",
                maxWidth: "300px",
                wordWrap: "break-word",
                whiteSpace: "normal"
              }}
            >
              {props.tooltip}
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "0",
                  height: "0",
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderTop: "6px solid #333"
                }}
              />
            </div>
          )}
        </div>
      )}
      <div className="d-flex flex-grow-1">&nbsp;</div>
      <KC_UIText weight={KC_Weights.bold} text={number} />
    </div>
  );
}
